import { Box, Tabs, Tab, Tooltip } from '@mui/material'
import { useScenarioComparison } from 'Features/Projects/Analysis/ScenarioComparison.data'
import { useParams } from 'react-router-dom'
import { Button } from 'Common/Supplyvue-ui'
import { useState, useMemo } from 'react'
import { BaseTable } from 'Features/Projects/Table/BaseTable.view'
import { GridColDef } from '@mui/x-data-grid-premium'
import { PageLayout } from 'Common/Layout/PageLayout'
import { Panel } from 'Common/Components/Panel'
import { useProjectData } from 'Features/Projects/Scenarios/Scenarios.data'

const tradeOffMetricsMapping = {
  weighted_service_level: 'Weighted Service Level',
  production_efficiency: 'Production Efficiency',
  bau_stock_duration: 'BAU Stock Duration',
}

const efficiencyMetricsMapping = {
  available_capacity_hours: 'Available Capacity',
  avg_utilised_time: 'Capacity Usage',
  avg_utilisation: 'Utilisation',
  avg_run_rate: 'Run Rate',
  avg_changeover_time: 'Changeover Time',
  pct_changeover_time: '%Changeover Time',
  avg_sku_changeovers_per_week: 'SKU Changeovers',
}

const cycleSpeedMetricsMapping = {
  weekly_throughput: 'Weekly Throughput',
  vol_weighted_cycle_time: 'Volume Weighted Cycle Time',
  avg_cycle_time: 'Average Cycle Time',
  foc_count: 'FOC Count',
  foq_count: 'FOQ Count',
  pct_foc_volume: 'PCT FOC Volume',
  pct_cycle_1_volume: 'PCT Cycle 1 Volume',
}

const inventoryMetricsMapping = {
  avg_stock_amount: 'Cycle Stock Amount',
  safety_stock_amount: 'Safety Stock Amount',
  avg_stock_duration: 'Cycle Stock Duration',
  daily_sales_rate: 'Daily Sales Rate',
  safety_stock_duration: 'Safety Stock Duration',
  coupled_count: 'Coupled Count',
  pct_coupled_count: 'PCT Coupled Count',
}

const assumptionMetricsMapping = {
  sales_rate_period_type: 'Sales Rate Period Type',
  period_start_week: 'Period Start Week',
  number_of_weeks: 'Number of Weeks',
}

type ScenarioComparisonParams = {
  projectId: string
}

type MetricRow = Record<string, unknown> & { Metric?: string }

/**
 * The comparison grid's columns: the metric's display name, then one column
 * per scenario. Built once per mapping — the five copies of this that sat in
 * the component differed only in the mapping.
 */
const metricColumns = (
  mapping: Record<string, string>,
  scenarios: string[],
  numeric: boolean
): GridColDef[] => [
  {
    field: 'Metric',
    headerName: 'Metric',
    width: 220,
    editable: false,
    cellClassName: 'metric-name',
    valueGetter: (value: string) => mapping[value] || value,
  },
  ...scenarios.map(
    (scenario): GridColDef => ({
      field: scenario,
      headerName: scenario,
      minWidth: 150,
      flex: 1,
      editable: false,
      headerAlign: numeric ? 'right' : 'left',
      align: numeric ? 'right' : 'left',
      cellClassName: numeric ? 'svp-num' : undefined,
    })
  ),
]

const rowsFor = (
  data: MetricRow[] | undefined,
  mapping: Record<string, string>
) =>
  data?.filter((item) =>
    Object.keys(mapping).includes(item.Metric as string)
  ) ?? []

const TABS = [
  {
    label: 'Efficiency',
    mapping: efficiencyMetricsMapping,
    numeric: true,
    file: 'efficiency-metrics',
  },
  {
    label: 'Cycle speed',
    mapping: cycleSpeedMetricsMapping,
    numeric: true,
    file: 'cycle-speed-metrics',
  },
  {
    label: 'Inventory',
    mapping: inventoryMetricsMapping,
    numeric: true,
    file: 'inventory-metrics',
  },
  {
    label: 'Assumptions',
    mapping: assumptionMetricsMapping,
    numeric: false,
    file: 'assumptions',
  },
]

const GRID_SX = {
  '& .metric-name': { fontWeight: 600, color: 'var(--ink-900)' },
}

/**
 * Every scenario in the project side by side (`analysis/scenario-comparison`):
 * the headline trade-offs in a panel of their own, then the detailed metrics
 * behind four tabs. Read-only.
 */
export const ScenarioComparisonView = () => {
  const { projectId } = useParams<ScenarioComparisonParams>() as {
    projectId: string
  }
  const [tabValue, setTabValue] = useState(0)

  const { data, isLoading } = useScenarioComparison(projectId)
  const { data: projectData } = useProjectData(projectId)

  const availableScenarios = useMemo(() => {
    const keysToExclude = ['Metric', 'id', 'Category']
    if (!data) return []
    const scenarios = new Set<string>()
    data.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if (!keysToExclude.includes(key)) {
          scenarios.add(key)
        }
      })
    })
    return Array.from(scenarios)
  }, [data])

  const tradeOffColumns = useMemo(
    () => metricColumns(tradeOffMetricsMapping, availableScenarios, true),
    [availableScenarios]
  )
  const tab = (TABS[tabValue] ?? TABS[0]) as (typeof TABS)[number]
  const tabColumns = useMemo(
    () => metricColumns(tab.mapping, availableScenarios, tab.numeric),
    [tab, availableScenarios]
  )

  // Read-only grids: BaseTable still requires these.
  const noop = () => {}

  return (
    <PageLayout
      title="Scenario comparison"
      subtitle={projectData?.projectName}
      actions={
        // The export was a console.log. Kept visible so the intent is not
        // lost, but disabled and explained rather than silently doing nothing.
        <Tooltip title="Export is not available yet">
          <span>
            <Button variant="outlined" disabled>
              Export
            </Button>
          </span>
        </Tooltip>
      }
    >
      <Panel title="Scenario trade-offs" bodyPadding="0">
        <Box sx={{ height: 200 }}>
          <BaseTable
            data={rowsFor(data, tradeOffMetricsMapping)}
            isLoading={isLoading}
            updateTableData={noop}
            isUploading={false}
            isSaving={false}
            checkBoxSelection={false}
            showTableToolBar={false}
            columns={tradeOffColumns}
            downloadFileName="scenario-tradeoffs"
            hideFooter
            sx={{ ...GRID_SX, border: 'none', borderRadius: 0 }}
          />
        </Box>
      </Panel>

      <Box>
        <Tabs
          value={tabValue}
          onChange={(_event, value: number) => setTabValue(value)}
          aria-label="Scenario comparison metrics"
        >
          {TABS.map((item) => (
            <Tab key={item.label} label={item.label} />
          ))}
        </Tabs>
        <Box
          role="tabpanel"
          aria-label={tab.label}
          sx={{ pt: '12px', height: 400 }}
        >
          <BaseTable
            key={tab.label}
            data={rowsFor(data, tab.mapping)}
            isLoading={isLoading}
            updateTableData={noop}
            isUploading={false}
            isSaving={false}
            checkBoxSelection={false}
            showTableToolBar={false}
            columns={tabColumns}
            downloadFileName={tab.file}
            hideFooter
            sx={GRID_SX}
          />
        </Box>
      </Box>
    </PageLayout>
  )
}
